import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStorage } from '../../hooks/useStorage';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../../components/ImageWithFallback';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiUpload, FiAlertCircle, FiEdit, FiTrash2, FiPlus, FiX, FiImage, FiVideo, FiCalendar, FiTag, FiStar, FiFolder } = FiIcons;

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Media library state
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryType, setLibraryType] = useState('image');

  const { uploadFile, uploading, error: uploadError } = useStorage();
  const navigate = useNavigate();

  const emptyItem = { 
    title: '', 
    type: 'image', 
    url: '', 
    date: new Date().toISOString().split('T')[0],
    tags: [] // Array of strings
  };
  
  const [formData, setFormData] = useState(emptyItem);
  const [tagsInput, setTagsInput] = useState(''); // Separate state for the tags input field
  const [mediaFile, setMediaFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('gallery_items_aox1234')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching gallery items:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openMediaLibrary = async (type) => {
    try {
      setLibraryType(type);
      setShowLibrary(true);
      setLibraryLoading(true);

      const folder = type === 'video' ? 'gallery/videos' : 'gallery/images';

      const { data, error } = await supabase.storage
        .from('team_logos')
        .list(folder, {
          limit: 100,
          sortBy: { column: 'name', order: 'desc' }
        });

      if (error) throw error;

      const items = (data || [])
        .filter(file => file.name !== '.emptyFolderPlaceholder')
        .map(file => {
          const path = `${folder}/${file.name}`;
          const { data: publicData } = supabase.storage.from('team_logos').getPublicUrl(path);
          return { name: file.name, path, url: publicData.publicUrl };
        });

      setLibraryItems(items);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Σφάλμα κατά τη φόρτωση της βιβλιοθήκης πολυμέσων' });
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleSelectFromLibrary = (item) => {
    setFormData(prev => ({
      ...prev,
      type: libraryType,
      url: item.url
    }));

    if (libraryType === 'image') {
      setPreviewUrl(item.url);
    } else {
      setPreviewUrl(null);
    }

    setMediaFile(null);
    setShowLibrary(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTagsChange = (e) => {
    setTagsInput(e.target.value);
  };

  const handleMediaChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileType = file.type.startsWith('video/') ? 'video' : 'image';
      
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const allowedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
      const allowedTypes = [...allowedImageTypes, ...allowedVideoTypes];

      if (!allowedTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Παρακαλώ ανεβάστε μια έγκυρη εικόνα ή βίντεο' });
        return;
      }

      const maxSize = fileType === 'video' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setMessage({ type: 'error', text: `Το αρχείο είναι πολύ μεγάλο. Παρακαλώ ανεβάστε αρχείο μικρότερο από ${fileType === 'video' ? '50MB' : '10MB'}.` });
        return;
      }

      setMediaFile(file);
      setFormData(prev => ({ ...prev, type: fileType }));

      if (fileType === 'image') {
        const fileReader = new FileReader();
        fileReader.onload = (event) => {
          setPreviewUrl(event.target.result);
        };
        fileReader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      let mediaUrl = formData.url;

      if (mediaFile) {
        console.log('Uploading new media file...');
        const folder = formData.type === 'video' ? 'videos' : 'images';
        const uploadedUrl = await uploadFile(mediaFile, 'team_logos', `gallery/${folder}/`);
        if (uploadedUrl) {
          mediaUrl = uploadedUrl;
          console.log('Media uploaded successfully:', mediaUrl);
        } else {
          throw new Error(uploadError || 'Failed to upload media');
        }
      }

      // Process tags from input string to array
      const processedTags = tagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '');

      const itemData = {
        title: formData.title,
        type: formData.type,
        url: mediaUrl,
        date: formData.date,
        tags: processedTags,
        updated_at: new Date().toISOString()
      };

      let result;
      if (editingId) {
        result = await supabase
          .from('gallery_items_aox1234')
          .update(itemData)
          .eq('id', editingId);
      } else {
        result = await supabase
          .from('gallery_items_aox1234')
          .insert([{ ...itemData, created_at: new Date().toISOString() }]);
      }

      if (result.error) throw result.error;

      setMessage({
        type: 'success',
        text: editingId ? 'Το στοιχείο ενημερώθηκε επιτυχώς!' : 'Το νέο στοιχείο προστέθηκε επιτυχώς!'
      });

      resetForm();
      fetchGalleryItems();
    } catch (err) {
      console.error('Error saving gallery item:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα: ${err.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}`
      });
    }
  };

  const handleEdit = (item) => {
    const formattedDate = item.date ? new Date(item.date).toISOString().split('T')[0] : '';
    setFormData({ ...item, date: formattedDate });
    setTagsInput((item.tags || []).join(', '));
    setEditingId(item.id);
    setShowForm(true);
    setPreviewUrl('');
    setMediaFile(null);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτό το στοιχείο;')) return;

    try {
      const { error } = await supabase
        .from('gallery_items_aox1234')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Το στοιχείο διαγράφηκε επιτυχώς!' });
      fetchGalleryItems();
    } catch (err) {
      console.error('Error deleting gallery item:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα κατά τη διαγραφή: ${err.message}`
      });
    }
  };

  const toggleAcademyTag = async (id, currentTags) => {
    try {
      const tags = currentTags || [];
      const hasTag = tags.includes('academy carousel');
      const newTags = hasTag ? tags.filter(t => t !== 'academy carousel') : [...tags, 'academy carousel'];

      const { error } = await supabase
        .from('gallery_items_aox1234')
        .update({ tags: newTags })
        .eq('id', id);

      if (error) throw error;
      fetchGalleryItems();
    } catch (err) {
      console.error('Error toggling tag:', err);
      alert('Σφάλμα κατά την ενημέρωση των ετικετών');
    }
  };

  const resetForm = () => {
    setFormData(emptyItem);
    setTagsInput('');
    setEditingId(null);
    setShowForm(false);
    setMediaFile(null);
    setPreviewUrl('');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('el-GR', options);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Πολυμέσα</h1>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className={`flex items-center px-4 py-2 ${showForm ? 'bg-gray-600' : 'bg-primary-600'} hover:bg-primary-700 text-white rounded-md`}
            >
              <SafeIcon icon={showForm ? FiX : FiPlus} className="w-5 h-5 mr-2" />
              {showForm ? 'Ακύρωση' : 'Προσθήκη Περιεχομένου'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-lg shadow-md p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingId ? 'Επεξεργασία Περιεχομένου' : 'Προσθήκη Νέου Περιεχομένου'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Τίτλος *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Ημερομηνία *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                  Ετικέτες (χωρισμένες με κόμμα)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiTag} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={tagsInput}
                    onChange={handleTagsChange}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="π.χ. academy carousel, homepage, action"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Χρησιμοποιήστε την ετικέτα <strong>academy carousel</strong> για να εμφανιστεί η εικόνα στο banner της ακαδημίας στην αρχική σελίδα.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Τύπος Περιεχομένου *
                </label>
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="type-image"
                      name="type"
                      value="image"
                      checked={formData.type === 'image'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="type-image" className="ml-2 block text-sm text-gray-700">
                      Εικόνα
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="type-video"
                      name="type"
                      value="video"
                      checked={formData.type === 'video'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="type-video" className="ml-2 block text-sm text-gray-700">
                      Βίντεο
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.type === 'image' ? 'Εικόνα' : 'Βίντεο'} *
                </label>
                <div className="flex items-start space-x-4">
                  {formData.type === 'image' ? (
                    <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border">
                      <ImageWithFallback src={previewUrl || formData.url} alt={formData.title} className="max-w-full max-h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border">
                      <SafeIcon icon={FiVideo} className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer bg-white border border-gray-300 rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:bg-gray-50 mb-2">
                      <SafeIcon icon={FiUpload} className="w-5 h-5 mr-2" />
                      {mediaFile ? `Αλλαγή ${formData.type === 'image' ? 'εικόνας' : 'βίντεο'}` : `Επιλογή ${formData.type === 'image' ? 'εικόνας' : 'βίντεο'}`}
                      <input type="file" className="sr-only" accept={formData.type === 'image' ? 'image/*' : 'video/*' } onChange={handleMediaChange} />
                    </label>
                    
                    {/* Choose existing button */}
                    <button
                      type="button"
                      onClick={() => openMediaLibrary(formData.type)}
                      className="w-full mt-1 bg-white border border-gray-300 rounded-md py-2 px-3 flex items-center justify-center text-sm font-medium text-gray-700 hover:bg-gray-50 mb-2"
                    >
                      <SafeIcon icon={FiFolder} className="w-5 h-5 mr-2" />
                      Επιλογή από ήδη ανεβασμένα
                    </button>

                    {mediaFile && (
                      <p className="text-sm text-green-600 mb-2">
                        Επιλέχθηκε: {mediaFile.name}
                      </p>
                    )}
                    {!mediaFile && formData.url && (
                      <p className="text-sm text-gray-600 mb-2">
                        Υπάρχον αρχείο: {formData.url.split('/').pop()}
                      </p>
                    )}
                    <p className="text-xs text-gray-500">
                      {formData.type === 'image' 
                        ? 'Προτεινόμενο μέγεθος: 1200x800 pixels. Μορφές: JPG, PNG, GIF, WebP (μέγιστο 10MB)'
                        : 'Μορφές: MP4, WebM, OGG (μέγιστο 50MB)'
                      }
                    </p>
                    <div className="mt-4">
                      <label htmlFor="url" className="block text-xs font-medium text-gray-500 mb-1">
                        Ή εισάγετε URL
                      </label>
                      <input
                        type="text"
                        id="url"
                        name="url"
                        value={formData.url}
                        onChange={handleInputChange}
                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder={`URL ${formData.type === 'image' ? 'εικόνας' : 'βίντεο'}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingId ? 'Ενημέρωση' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Πολυμέσα</h2>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Φόρτωση...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-4 rounded-md text-red-700">
              <p>{error}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg overflow-hidden shadow-md relative group h-full flex flex-col"
                >
                  <div className="relative">
                    <div className="absolute top-2 right-2 space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 bg-blue-100 rounded-full hover:bg-blue-200 text-blue-600"
                        title="Επεξεργασία"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1 bg-red-100 rounded-full hover:bg-red-200 text-red-600"
                        title="Διαγραφή"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="aspect-w-16 aspect-h-9 h-48 bg-gray-200 relative">
                      {item.type === 'image' ? (
                        <ImageWithFallback src={item.url} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                          <SafeIcon icon={FiVideo} className="w-12 h-12" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.type === 'video' ? 'bg-red-500' : 'bg-blue-500'}`}>
                          <SafeIcon icon={item.type === 'video' ? FiVideo : FiImage} className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold text-gray-900 mb-1 truncate">{item.title}</h3>
                    <div className="flex items-center text-gray-500 text-xs mb-3">
                      <SafeIcon icon={FiCalendar} className="w-3 h-3 mr-1" />
                      <span>{formatDate(item.date)}</span>
                    </div>
                    
                    {/* Tags display */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(item.tags || []).map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="mt-auto">
                      <button
                        type="button"
                        onClick={() => toggleAcademyTag(item.id, item.tags)}
                        className={`w-full px-2 py-1.5 text-xs rounded-md border transition-colors flex items-center justify-center ${
                          (item.tags || []).includes('academy carousel')
                            ? 'bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-200'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <SafeIcon icon={FiStar} className={`w-3 h-3 mr-1 ${(item.tags || []).includes('academy carousel') ? 'fill-current' : ''}`} />
                        {(item.tags || []).includes('academy carousel') ? 'Στο Academy Carousel' : 'Προσθήκη στο Carousel'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          
          {!loading && items.length === 0 && (
            <p className="text-gray-500 text-center py-4">Δεν υπάρχουν καταχωρηρημένα πολυμέσα</p>
          )}
        </div>
      </div>

      {/* Media Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
            
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Επιλογή {libraryType === 'image' ? 'εικόνας' : 'βίντεο'} από βιβλιοθήκη
              </h3>
              <button
                onClick={() => setShowLibrary(false)}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-auto flex-1">
              {libraryLoading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
                  <p className="mt-2 text-sm text-gray-600">Φόρτωση πολυμέσων...</p>
                </div>
              ) : libraryItems.length === 0 ? (
                <p className="text-sm text-gray-500 text-center">
                  Δεν βρέθηκαν {libraryType === 'image' ? 'εικόνες' : 'βίντεο'} στη βιβλιοθήκη.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {libraryItems.map((file) => (
                    <button
                      key={file.path}
                      type="button"
                      onClick={() => handleSelectFromLibrary(file)}
                      className="group relative border rounded-lg overflow-hidden bg-gray-50 hover:border-primary-500 focus:outline-none w-full text-left"
                    >
                      {libraryType === 'image' ? (
                        <div className="aspect-w-16 aspect-h-9">
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-full h-32 object-cover group-hover:opacity-90"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-32 flex items-center justify-center bg-gray-800 text-white">
                          <SafeIcon icon={FiVideo} className="w-8 h-8" />
                        </div>
                      )}
                      <div className="absolute bottom-0 inset-x-0 bg-black/50 text-xs text-white px-2 py-1 truncate">
                        {file.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;